import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import {
  GanttComponent,
  ColumnsDirective,
  ColumnDirective,
  Inject,
  Selection,
  type GanttComponent as GanttComponentType,
  type TimelineSettingsModel
} from '@syncfusion/ej2-react-gantt';
import type { GanttVisualizationProfileDto } from '../api';
import type {
  ProductionScheduleRendererProps,
  ProductionScheduleViewHandle
} from './ProductionScheduleViewTypes';
import { getZoomIntervalMinutes } from './ProductionScheduleViewTypes';

export type { ProductionScheduleViewHandle };

/** Lightens a color by blending with white (0–1, higher = lighter). */
function lightenColor(color: string, amount: number): string {
  if (!color || typeof color !== 'string') return '#e8f5e9';
  let r = 0, g = 0, b = 0;
  const hex = color.replace(/^#/, '');
  if (hex.length === 6 || hex.length === 8) {
    r = parseInt(hex.slice(0, 2), 16) / 255;
    g = parseInt(hex.slice(2, 4), 16) / 255;
    b = parseInt(hex.slice(4, 6), 16) / 255;
  } else {
    const rgb = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
    if (rgb) {
      r = parseInt(rgb[1], 10) / 255;
      g = parseInt(rgb[2], 10) / 255;
      b = parseInt(rgb[3], 10) / 255;
    } else {
      return '#e8f5e9';
    }
  }
  const t = Math.max(0, Math.min(1, amount));
  const r2 = Math.round((r + (1 - r) * t) * 255);
  const g2 = Math.round((g + (1 - g) * t) * 255);
  const b2 = Math.round((b + (1 - b) * t) * 255);
  return `#${r2.toString(16).padStart(2, '0')}${g2.toString(16).padStart(2, '0')}${b2.toString(16).padStart(2, '0')}`;
}

/** Returns black or white for optimal contrast on the given background color. */
function getContrastTextColor(bgColor: string): string {
  if (!bgColor || typeof bgColor !== 'string') return '#1a1a1a';
  let r = 0, g = 0, b = 0;
  const hex = bgColor.replace(/^#/, '');
  if (hex.length === 6 || hex.length === 8) {
    r = parseInt(hex.slice(0, 2), 16) / 255;
    g = parseInt(hex.slice(2, 4), 16) / 255;
    b = parseInt(hex.slice(4, 6), 16) / 255;
  } else {
    const rgb = bgColor.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
    if (rgb) {
      r = parseInt(rgb[1], 10) / 255;
      g = parseInt(rgb[2], 10) / 255;
      b = parseInt(rgb[3], 10) / 255;
    } else {
      return '#1a1a1a';
    }
  }
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return luminance > 0.5 ? '#1a1a1a' : '#ffffff';
}

const ProductionScheduleGanttRenderer = forwardRef<
  ProductionScheduleViewHandle,
  ProductionScheduleRendererProps
>(
  (
    {
      timeWindow,
      operations,
      setups = [],
      resources,
      zoomLevel = '10min',
      onVisualProfileChange,
      onZoomToFit,
      height = '100%',
      width = '100%',
      readonly = true
    },
    ref
  ) => {
    const ganttRef = useRef<GanttComponentType>(null);
    const didCollapseRef = useRef(false);
    const rowHeight = 52;
    const taskbarHeight = 36;
    const normalizedTimeWindow = useMemo(() => {
      let start = new Date(timeWindow.start);
      let end = new Date(timeWindow.end);
      if (Number.isNaN(start.getTime())) {
        start = new Date();
      }
      if (Number.isNaN(end.getTime())) {
        end = new Date(start.getTime() + 60 * 60 * 1000);
      }
      if (end <= start) {
        end = new Date(start.getTime() + 60 * 60 * 1000);
      }
      return { start, end };
    }, [timeWindow]);
    const zoomMinutes = useMemo(
      () => getZoomIntervalMinutes(zoomLevel),
      [zoomLevel]
    );
    const roundedTimeWindow = useMemo(() => {
      const intervalMs = zoomMinutes * 60 * 1000;
      const startMs = normalizedTimeWindow.start.getTime();
      const endMs = normalizedTimeWindow.end.getTime();
      const roundedStart = new Date(Math.floor(startMs / intervalMs) * intervalMs);
      const roundedEnd = new Date(Math.ceil(endMs / intervalMs) * intervalMs);
      if (roundedEnd <= roundedStart) {
        return { start: roundedStart, end: new Date(roundedStart.getTime() + intervalMs) };
      }
      return { start: roundedStart, end: roundedEnd };
    }, [normalizedTimeWindow, zoomMinutes]);
    const projectStartDate = roundedTimeWindow.start;
    const projectEndDate = roundedTimeWindow.end;

    const toResourceId = useCallback((value: unknown): string | number | undefined => {
      if (value == null) return undefined;
      if (typeof value === 'object' && value !== null && 'id' in value) {
        return (value as { id: unknown }).id as string | number;
      }
      return value as string | number;
    }, []);

    const ganttResources = useMemo(() => {
      const base = resources.map(resource => ({
        id: resource.id,
        name: resource.name ?? '',
        type: resource.type ?? '',
        isExpand: false
      }));
      const resourceIdsWithOps = new Set(
        operations.flatMap(op => {
          const rid = toResourceId(op.resourceId);
          return rid != null ? [String(rid)] : [];
        })
      );
      const resourceIdsWithSetups = new Set(
        setups.flatMap(s => {
          const rid = toResourceId(s.resourceId);
          return rid != null ? [String(rid)] : [];
        })
      );
      const allResourceIds = new Set([...resourceIdsWithOps, ...resourceIdsWithSetups]);
      if (operations.length === 0 && setups.length === 0) return base;
      const filtered = base.filter(r => allResourceIds.has(String(r.id)));
      return filtered.length > 0 ? filtered : base;
    }, [resources, operations, setups, toResourceId]);

    const ganttOperations = useMemo(
      () =>
        operations.map(operation => {
          const rid = toResourceId(operation.resourceId);
          const start = operation.startTime instanceof Date ? operation.startTime : new Date(operation.startTime);
          const end = operation.endTime instanceof Date ? operation.endTime : new Date(operation.endTime);
          const durationMinutes = Math.max(1, Math.round((end.getTime() - start.getTime()) / 60000));
          const subj = operation.subject ?? '';
          const opName = operation.operationName ?? subj;
          return {
            id: `op-${operation.id}`,
            subject: subj,
            operationName: opName,
            taskName: subj || opName,
            orderNumber: operation.orderNumber ?? undefined,
            productName: operation.productName ?? undefined,
            campaignId: operation.campaignId ?? undefined,
            startDate: start,
            endDate: end,
            startTime: start,
            endTime: end,
            plannedStart: start,
            plannedEnd: end,
            duration: durationMinutes,
            durationUnit: 'minute' as const,
            resourceIds: rid != null ? [rid] : [],
            color: operation.color ?? undefined,
            progress: 0
          };
        }),
      [operations, toResourceId]
    );

    const ganttSetups = useMemo(
      () =>
        setups.map(setup => {
          const rid = toResourceId(setup.resourceId);
          const start = setup.startTime instanceof Date ? setup.startTime : new Date(setup.startTime);
          const end = setup.endTime instanceof Date ? setup.endTime : new Date(setup.endTime);
          const durationMinutes = Math.max(1, Math.round((end.getTime() - start.getTime()) / 60000));
          const subj = setup.subject ?? '';
          return {
            id: `setup-${setup.id}`,
            subject: subj,
            taskName: subj,
            startDate: start,
            endDate: end,
            startTime: start,
            endTime: end,
            duration: durationMinutes,
            durationUnit: 'minute' as const,
            resourceIds: rid != null ? [rid] : [],
            color: setup.color ?? undefined,
            progress: 0,
            productName: setup.productName ?? undefined
          };
        }),
      [setups, toResourceId]
    );

    const ganttData = useMemo(() => {
      const combined = [...ganttOperations, ...ganttSetups];
      if (combined.length > 0) {
        return combined;
      }
      const placeholderDuration = Math.max(
        1,
        Math.round((projectEndDate.getTime() - projectStartDate.getTime()) / 60000)
      );
      return [
        {
          id: 'placeholder-task',
          subject: 'TimeWindow',
          taskName: 'TimeWindow',
          startDate: projectStartDate,
          endDate: projectEndDate,
          startTime: projectStartDate,
          endTime: projectEndDate,
          duration: placeholderDuration,
          durationUnit: 'minute' as const,
          resourceIds: ganttResources[0] ? [ganttResources[0].id] : [],
          progress: 0
        }
      ];
    }, [ganttOperations, ganttSetups, ganttResources, projectEndDate, projectStartDate]);

    useEffect(() => {
      didCollapseRef.current = false;
    }, [ganttData, ganttResources]);

    const timelineSettings = useMemo<TimelineSettingsModel>(() => {
      const minutes = zoomMinutes;
      const timelineUnitSize = 60;
      return {
        timelineViewMode: 'Minutes' as const,
        topTier: { unit: 'Day' as const, count: 1, format: 'dd.MM.yyyy' },
        bottomTier: { unit: 'Minutes' as const, count: minutes, format: 'HH:mm' },
        timelineUnitSize,
        weekStartDay: 1
      };
    }, [zoomMinutes]);
    const resourceTypeById = useMemo(
      () => new Map(resources.map(resource => [resource.id, resource.type || ''])),
      [resources]
    );
    const resourceTypeByName = useMemo(
      () => new Map(resources.map(resource => [resource.name, resource.type || ''])),
      [resources]
    );

    const renderShiftBackgrounds = useCallback(() => {
      const ganttElement = ganttRef.current?.element as HTMLElement | undefined;
      if (!ganttElement) return;
      const chartRows = Array.from(ganttElement.querySelectorAll('.e-chart-row')) as HTMLElement[];
      if (chartRows.length === 0) return;

      const windowStartMs = projectStartDate.getTime();
      const windowEndMs = projectEndDate.getTime();
      const windowDurationMs = windowEndMs - windowStartMs;
      if (windowDurationMs <= 0) return;

      chartRows.forEach((row, index) => {
        row.querySelectorAll('.psv-shift-background').forEach(node => node.remove());
        const resource = resources[index];
        const shifts = resource?.shifts ?? [];
        if (!resource || shifts.length === 0) return;

        const rowWidth = row.clientWidth;
        if (rowWidth <= 0) return;

        row.style.position = 'relative';
        const layer = document.createElement('div');
        layer.className = 'psv-shift-background';

        shifts.forEach(shift => {
          const shiftStart = new Date(shift.start).getTime();
          const shiftEnd = new Date(shift.end).getTime();
          if (Number.isNaN(shiftStart) || Number.isNaN(shiftEnd)) return;

          const clampedStart = Math.max(shiftStart, windowStartMs);
          const clampedEnd = Math.min(shiftEnd, windowEndMs);
          if (clampedEnd <= clampedStart) return;

          const left = ((clampedStart - windowStartMs) / windowDurationMs) * rowWidth;
          const w = ((clampedEnd - clampedStart) / windowDurationMs) * rowWidth;

          const segment = document.createElement('div');
          segment.className = `psv-shift-segment${
            shift.type === 'off'
              ? ' psv-shift-off'
              : shift.type === 'exception'
                ? ' psv-shift-exception'
                : ''
          }`;
          segment.style.left = `${left}px`;
          segment.style.width = `${w}px`;
          layer.appendChild(segment);
        });

        if (layer.childElementCount > 0) {
          row.prepend(layer);
        }
      });
    }, [projectEndDate, projectStartDate, resources]);

    useEffect(() => {
      const handle = requestAnimationFrame(() => {
        renderShiftBackgrounds();
      });
      return () => cancelAnimationFrame(handle);
    }, [renderShiftBackgrounds, timelineSettings]);

    const handleDataBound = useCallback(() => {
      const gantt = ganttRef.current as any;
      const tooltip = gantt?.tooltipModule?.toolTipObj;
      if (tooltip) {
        tooltip.enableHtmlParse = true;
        const tooltipModule = gantt.tooltipModule;
        const origAfterOpen = tooltip.afterOpen;
        tooltip.afterOpen = (e: unknown) => {
          origAfterOpen?.call(tooltipModule, e);
          const contentEl = (tooltip as any).tooltipEle?.querySelector('.e-tip-content');
          if (contentEl) {
            const raw = contentEl.textContent || '';
            if (raw.includes('<div') && raw.includes('e-tooltip-content')) {
              contentEl.innerHTML = raw;
            }
          }
        };
      }
      renderShiftBackgrounds();
      if (!didCollapseRef.current) {
        const gantt = ganttRef.current;
        if (gantt) {
          didCollapseRef.current = true;
          requestAnimationFrame(() => {
            try {
              gantt.collapseAll();
            } catch {
              // The Syncfusion ResourceView can fail on placeholder data
            }
          });
        }
      }
    }, [renderShiftBackgrounds]);

    const handleQueryCellInfo = useCallback((args: any) => {
      if (args?.column?.field !== 'name') return;
      const name = args?.data?.name ?? '';
      const type =
        args?.data?.type ??
        resourceTypeById.get(args?.data?.id) ??
        resourceTypeByName.get(name) ??
        '';
      if (!type) return;

      const cell = args.cell as HTMLElement | undefined;
      if (!cell) return;

      cell.innerHTML = '';
      cell.style.boxSizing = 'border-box';
      cell.style.padding = '8px';
      cell.style.display = 'flex';
      cell.style.flexDirection = 'column';
      cell.style.justifyContent = 'center';
      cell.style.gap = '1px';
      cell.style.whiteSpace = 'normal';
      const nameDiv = document.createElement('div');
      nameDiv.textContent = name;
      nameDiv.style.fontWeight = '600';
      nameDiv.style.display = 'block';
      nameDiv.style.lineHeight = '1.1';
      nameDiv.style.whiteSpace = 'nowrap';
      nameDiv.style.overflow = 'hidden';
      nameDiv.style.textOverflow = 'ellipsis';
      nameDiv.style.maxWidth = '100%';
      const typeDiv = document.createElement('div');
      typeDiv.textContent = type;
      typeDiv.style.fontSize = '12px';
      typeDiv.style.color = '#666';
      typeDiv.style.display = 'block';
      typeDiv.style.lineHeight = '1.1';
      typeDiv.style.whiteSpace = 'nowrap';
      typeDiv.style.overflow = 'hidden';
      typeDiv.style.textOverflow = 'ellipsis';
      typeDiv.style.maxWidth = '100%';
      cell.appendChild(nameDiv);
      cell.appendChild(typeDiv);
    }, [resourceTypeById, resourceTypeByName]);

    const handleQueryTaskbarInfo = useCallback((args: any) => {
      // Hide manual parent taskbar (gray line + connector points) per Syncfusion KB 8525
      if (args?.taskbarType === 'ParentTask') {
        const el = args.taskbarElement as HTMLElement | undefined;
        if (el) {
          const manualParent = el.classList?.contains('e-manualparent-main-container')
            ? el
            : el.querySelector?.('.e-manualparent-main-container') as HTMLElement | null;
          if (manualParent) {
            manualParent.style.display = 'none';
          } else {
            el.style.display = 'none';
          }
        }
        return;
      }
      const color = args?.data?.color;
      if (!color) return;
      args.taskbarBgColor = color;
      args.progressBarBgColor = color;
      /* No Syncfusion border - our taskbarTemplate has its own border (one per color) */
      args.taskbarBorderColor = 'transparent';
    }, []);

    const taskbarTemplate = useCallback((props: any) => {
      const data = props?.data ?? props?.ganttProperties ?? props;
      const raw = data?.taskData ?? data ?? props?.taskData ?? props;
      const baseColor = raw?.color ?? data?.color ?? props?.taskData?.color ?? '#4CAF50';
      const bgColor = lightenColor(baseColor, 0.75);
      const textColor = getContrastTextColor(bgColor);
      const productName = raw?.productName ?? data?.productName ?? '';
      const operationName =
        raw?.operationName ?? data?.operationName ?? raw?.taskName ?? raw?.subject ?? data?.subject ?? '';
      return (
        <div
          className="e-gantt-child-taskbar-inner-div e-gantt-child-taskbar"
          style={{
            backgroundColor: bgColor,
            color: textColor,
            border: `1px solid ${baseColor}cc`,
            height: '100%',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            justifyContent: 'center',
            padding: '0 6px',
            borderRadius: '4px',
            fontSize: '11px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            lineHeight: 1.2,
            boxSizing: 'border-box'
          }}
        >
          <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>{productName || '–'}</span>
          <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>{operationName || '–'}</span>
        </div>
      );
    }, []);

    const tooltipTaskbarFn = useCallback((data: any) => {
      const gp = data?.ganttProperties;
      const task =
        gp ||
        (Array.isArray(data?.childRecords) && data.childRecords[0]?.ganttProperties
          ? data.childRecords[0].ganttProperties
          : null) ||
        data;
      const raw = data?.taskData ?? data;
      const productName = raw?.productName ?? data?.productName ?? '';
      const executionSeq = raw?.executionSequenceNumber ?? data?.executionSequenceNumber;
      const operationName =
        raw?.operationName ?? data?.operationName ?? raw?.taskName ?? raw?.subject ?? task?.taskName ?? '';
      const productLine =
        executionSeq != null
          ? `Produkt: ${productName || '–'} (${executionSeq})`
          : `Produkt: ${productName || '–'}`;
      const start = (globalThis as any).formatTaskbarDateTime(
        task?.startDate ?? raw?.startDate ?? data?.startDate
      );
      const end = (globalThis as any).formatTaskbarDateTime(
        task?.endDate ?? raw?.endDate ?? data?.endDate
      );
      const duration = task?.duration ?? raw?.duration ?? data?.duration ?? 0;
      const progress = (globalThis as any).formatTaskbarProgress(
        task?.progress ?? raw?.progress ?? data?.progress
      );
      return `<div class="e-tooltip-content"><div class="e-subject">${productLine}</div><div>Vorgang: ${operationName}</div><div>Start: ${start}</div><div>Ende: ${end}</div><div>Dauer: ${duration} Minuten</div><div>Fortschritt: ${progress}</div></div>`;
    }, []);

    const tooltipTimelineFn = useCallback((data: any) => {
      const formatted = (globalThis as any).formatTimelineDate(
        data?.date,
        data?.startDate,
        data?.value
      );
      return `<div class="e-tooltip-content">${formatted}</div>`;
    }, []);

    const tooltipSettings = useMemo(
      () => ({
        showTooltip: true,
        taskbar: tooltipTaskbarFn,
        timeline: tooltipTimelineFn
      }),
      [tooltipTaskbarFn, tooltipTimelineFn]
    );

    const handleBeforeTooltipRender = useCallback((args: any) => {
      if (args?.content && typeof args.content === 'string') {
        const wrapper = document.createElement('div');
        wrapper.innerHTML = args.content;
        args.content = wrapper;
      }
    }, []);

    useImperativeHandle(
      ref,
      () => ({
        scrollToStart: () => {
          // Placeholder for future Gantt scrolling behavior.
        },
        scrollToEnd: () => {
          // Placeholder for future Gantt scrolling behavior.
        },
        scrollToNow: () => {
          // Placeholder for future Gantt scrolling behavior.
        },
        zoomToFit: () => {
          onZoomToFit?.();
        },
        setVisualProfile: (profile: GanttVisualizationProfileDto) => {
          onVisualProfileChange?.(profile);
        }
      }),
      [onZoomToFit, onVisualProfileChange]
    );

    return (
      <>
        <style>
          {`
            .e-gantt .e-grid .e-rowcell {
              box-sizing: border-box;
              padding: 4px 8px;
              overflow: hidden;
            }
            .e-gantt .e-grid .e-row,
            .e-gantt .e-chart-row {
              min-height: ${rowHeight}px;
              height: ${rowHeight}px !important;
              box-sizing: border-box;
            }
            .e-gantt .e-chart-row {
              position: relative;
              border-bottom-color: transparent;
            }
            .e-gantt .e-manualparent-main-container {
              display: none !important;
            }
            .psv-shift-background {
              position: absolute;
              left: 0;
              top: 0;
              right: 0;
              bottom: 1px;
              pointer-events: none;
              z-index: 0;
            }
            .psv-shift-segment {
              position: absolute;
              top: 0;
              height: 100%;
              background-color: rgba(120, 160, 255, 0.08);
            }
            .psv-shift-segment.psv-shift-exception {
              background-color: rgba(255, 180, 120, 0.1);
            }
            .psv-shift-segment.psv-shift-off {
              background-color: rgba(160, 160, 160, 0.1);
            }
            .e-gantt .e-taskbar-main-container {
              position: relative;
              z-index: 1;
            }
            /* Syncfusion-Wrapper-Rahmen entfernen – nur unser Template-Rahmen (pro Farbe) sichtbar */
            .e-gantt .e-gantt-child-taskbar-main-container,
            .e-gantt .e-gantt-child-taskbar:not(.e-gantt-child-taskbar-inner-div) {
              border: none !important;
            }
          `}
        </style>
        <GanttComponent
          ref={ganttRef}
          locale="de-DE"
          width={width}
          height={height}
          dataSource={ganttData}
          resources={ganttResources}
          projectStartDate={projectStartDate}
          projectEndDate={projectEndDate}
          timelineSettings={timelineSettings}
          dataBound={handleDataBound}
          taskFields={{
            id: 'id',
            name: 'subject',
            startDate: 'startDate',
            endDate: 'endDate',
            duration: 'duration',
            durationUnit: 'durationUnit',
            resourceInfo: 'resourceIds',
            expandState: 'isExpand'
          }}
          resourceFields={{
            id: 'id',
            name: 'name'
          }}
          viewType="ResourceView"
          taskMode="Manual"
          dayWorkingTime={[{ from: 0, to: 24 }]}
          includeWeekend={true}
          renderBaseline={false}
          allowParentDependency={false}
          editSettings={{ allowTaskbarEditing: !readonly }}
          taskbarTemplate={taskbarTemplate}
          enableMultiTaskbar={true}
          allowSelection={!readonly}
          gridLines="Both"
          rowHeight={rowHeight}
          taskbarHeight={taskbarHeight}
          queryCellInfo={handleQueryCellInfo}
          queryTaskbarInfo={handleQueryTaskbarInfo}
          tooltipSettings={tooltipSettings as any}
          beforeTooltipRender={handleBeforeTooltipRender}
        >
          <ColumnsDirective>
            <ColumnDirective field="name" headerText="Resource" width="240" />
          </ColumnsDirective>
          <Inject services={[Selection]} />
        </GanttComponent>
      </>
    );
  }
);

ProductionScheduleGanttRenderer.displayName = 'ProductionScheduleGanttRenderer';

export default ProductionScheduleGanttRenderer;
